import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import useDashboardData from "../hooks/useDashboardData";
import EnhancedChart from "./EnhancedChart";
import "./Dashboard.css";

function SalesDashboard() {
  const {
    data: chartsData,
    loading,
    error,
    refetch,
  } = useDashboardData("/visualize/sales", { method: "POST" });
  const [stdSummary, setStdSummary] = useState(null);

  useEffect(() => {
    fetchStandardSummary();
  }, []);

  // If summary is present but shows zero insights while data exists, trigger a background /analyze once
  const [analysisTriggered, setAnalysisTriggered] = useState(false);
  useEffect(() => {
    const maybeTriggerAnalyze = async () => {
      if (analysisTriggered) return;
      if (!stdSummary) return;
      const counts = stdSummary.insight_counts || {};
      const total = Object.values(counts).reduce(
        (sum, v) => sum + (typeof v === "number" ? v : 0),
        0
      );
      const haveRecords = (stdSummary.records_analyzed || 0) > 0;
      if (haveRecords && total === 0) {
        try {
          setAnalysisTriggered(true);
          await fetch(
            "https://restaurant-analytics-backend.onrender.com/analyze",
            { method: "POST" }
          );
          await fetchStandardSummary();
        } catch (e) {
          // Ignore; dashboard will still function
        }
      }
    };
    maybeTriggerAnalyze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stdSummary]);

  const fetchSalesData = refetch;

  const fetchStandardSummary = async () => {
    try {
      const res = await fetch(
        "https://restaurant-analytics-backend.onrender.com/report/summary",
        {
          method: "GET",
        }
      );
      if (res.status === 404) return; // Analysis not run yet
      const data = await res.json();
      if (res.ok) {
        setStdSummary(data);
      } else {
        throw new Error(data.detail || "Failed to fetch standardized summary");
      }
    } catch (err) {
      console.error("Error fetching summary:", err.message || String(err));
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <h2>Loading Sales Analytics...</h2>
        <p>Fetching your sales data and generating insights</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h2>❌ Error Loading Sales Data</h2>
        <p>{error}</p>
        <button onClick={fetchSalesData} className="retry-btn">
          🔄 Retry
        </button>
      </div>
    );
  }

  if (!chartsData || !chartsData.charts) {
    return (
      <div className="dashboard-error">
        <h2>📊 No Sales Data Available</h2>
        <p>Please upload your sales data first to view analytics</p>
      </div>
    );
  }

  const { charts, data_summary } = chartsData;

  // Fallbacks for header stats when backend summary is missing
  const fallbackSalesStats = (() => {
    let records = data_summary?.total_records ?? null;
    let dateRange = data_summary?.date_range ?? null;

    // Try to infer from daily_sales_trend
    const daily = charts?.daily_sales_trend;
    const labels = daily?.data?.labels;
    const ds = daily?.data?.datasets?.[0]?.data;
    if (records == null) {
      if (Array.isArray(ds) && ds.length > 0) records = ds.length;
      else if (Array.isArray(labels) && labels.length > 0)
        records = labels.length;
    }
    if (!dateRange && Array.isArray(labels) && labels.length > 0) {
      const parsed = labels
        .map((v) => new Date(v))
        .filter((d) => !isNaN(d.getTime()))
        .sort((a, b) => a - b);
      if (parsed.length > 0) {
        dateRange = {
          start: parsed[0].toISOString(),
          end: parsed[parsed.length - 1].toISOString(),
        };
      }
    }
    return { records, dateRange };
  })();

  return (
    <motion.div
      className="dashboard"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <h1>📊 Sales Analytics Dashboard</h1>
        <p>Comprehensive analysis of your sales patterns and trends</p>

        {/* Standardized sales summary */}
        {stdSummary &&
          (() => {
            const summaries = stdSummary.summaries || {};
            const insightCounts = stdSummary.insight_counts || {};
            const sales = summaries.sales_patterns || {};
            const totalRevenue = sales.total_revenue ?? null;
            const records = stdSummary.records_analyzed ?? null;
            // Robust insight count fallback (prefer module-specific → AI → total)
            let insights = insightCounts.sales_patterns;
            if (insights == null || insights === 0) {
              const aiCount = insightCounts.ai_insights;
              if (typeof aiCount === "number" && aiCount > 0) {
                insights = aiCount;
              } else if (insights == null) {
                const total = Object.values(insightCounts || {}).reduce(
                  (sum, v) => sum + (typeof v === "number" ? v : 0),
                  0
                );
                if (total > 0) insights = total;
              }
              if (insights == null) {
                const summaryInsights =
                  sales.insights || stdSummary.insights || [];
                if (Array.isArray(summaryInsights))
                  insights = summaryInsights.length;
              }
            }
            return (
              <div className="summary-cards" style={{ marginTop: "0.5rem" }}>
                {records != null && (
                  <div className="summary-card">
                    <span className="summary-icon">📋</span>
                    <div className="summary-content">
                      <h3>{Number(records).toLocaleString()}</h3>
                      <p>Records Analyzed</p>
                    </div>
                  </div>
                )}
                {totalRevenue != null && (
                  <div className="summary-card">
                    <span className="summary-icon">💰</span>
                    <div className="summary-content">
                      <h3>₹{Number(totalRevenue).toLocaleString()}</h3>
                      <p>Total Revenue</p>
                    </div>
                  </div>
                )}
                {insights != null && (
                  <div className="summary-card">
                    <span className="summary-icon">💡</span>
                    <div className="summary-content">
                      <h3>{Number(insights).toLocaleString()}</h3>
                      <p>Insights</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-icon">📋</span>
            <div className="summary-content">
              <h3>
                {(() => {
                  const records = fallbackSalesStats.records ?? 0;
                  return Number(records).toLocaleString();
                })()}
              </h3>
              <p>Total Records</p>
            </div>
          </div>

          <div className="summary-card">
            <span className="summary-icon">📅</span>
            <div className="summary-content">
              <h3>
                {(() => {
                  const dr = fallbackSalesStats.dateRange;
                  if (dr?.start && dr?.end) {
                    try {
                      const startDate = new Date(dr.start).toLocaleDateString();
                      const endDate = new Date(dr.end).toLocaleDateString();
                      return `${startDate} - ${endDate}`;
                    } catch (e) {
                      return "Date range unavailable";
                    }
                  }
                  return "No date range";
                })()}
              </h3>
              <p>Date Range</p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="charts-grid"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        {charts.daily_sales_trend && (
          <EnhancedChart
            chartData={charts.daily_sales_trend}
            title="Daily Sales Trend"
            preferredType="auto"
            showMetrics={true}
          />
        )}
        {charts.weekly_sales_pattern && (
          <EnhancedChart
            chartData={charts.weekly_sales_pattern}
            title="Weekly Sales Pattern"
            preferredType="auto"
            showMetrics={true}
          />
        )}
        {charts.monthly_sales_comparison && (
          <EnhancedChart
            chartData={charts.monthly_sales_comparison}
            title="Monthly Sales Comparison"
            preferredType="auto"
            showMetrics={true}
          />
        )}
        {charts.peak_hours_analysis && (
          <EnhancedChart
            chartData={charts.peak_hours_analysis}
            title="Peak Hours Analysis"
            preferredType="auto"
            showMetrics={true}
          />
        )}
        {charts.hourly_sales_heatmap && (
          <EnhancedChart
            chartData={charts.hourly_sales_heatmap}
            title="Hourly Sales Heatmap"
            preferredType="plotly"
            showMetrics={false}
          />
        )}
      </motion.div>

      <div className="dashboard-actions">
        <button onClick={fetchSalesData} className="refresh-btn">
          🔄 Refresh Data
        </button>
      </div>
    </motion.div>
  );
}

export default SalesDashboard;
